import { Component } from 'global/interfaces/component.interface';
import ControllerBase from 'global/js/ControllerBase';
import ControllerProxy from 'global/js/ControllerProxy';
import Controller from '../../../global/js/controller';
import { fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import { MoleculeJobOfferAreaProps } from 'wolf/components/molecules/m-job-offer-area/m-job-offer-area.data';
import { MoleculeJobOfferSearchEvent, MoleculeJobOfferSearchEvents } from 'wolf/components/molecules/m-job-offer-search/m-job-offer-search.events';

const jobOffersTemplate = require('components/molecules/m-job-offer-area/m-job-offer-area.template.twig') as (
	data: Component<MoleculeJobOfferAreaProps>,
) => string;

interface ComponentRefs {
	jobOffersWrapper: 'jobOffersWrapper';
}

export interface FilterDataProps {
	selects: {
		key: string;
		value: string
	}[]
	search?: string;
}

export default class ControllerJobOffers extends ControllerBase<ComponentRefs> {
	private jobOffersUrl: string;
	private jobOffersWrapper: HTMLDivElement;
	private jobOffersContent: MoleculeJobOfferAreaProps[];

	constructor(element: HTMLElement) {
		super(element);
		this.jobOffersUrl = this.$element.dataset.jobOffersUrl || '';
		this.jobOffersWrapper = this.$references.jobOffersWrapper as HTMLDivElement;

		this.init();
	}

	private init() {
		this.filterContentBySelect();
		this.filterContentByText();
		this.clearFilters();
	}

	/**
	 * Makes an AJAX call if select field has changed
	 */
	private filterContentBySelect(): void {
		fromEvent(window, MoleculeJobOfferSearchEvents.inputChange)
			.pipe(
				map((event: CustomEvent<MoleculeJobOfferSearchEvent>) => {
					return {
						selects: event.detail.selects,
						search: event.detail.search || '',
					};
				}),
			)
			.subscribe((e) => {
				this.loadJobOffers(e)
					.then((response) => {
						this.jobOffersContent = response && response.data;
						this.renderSearchResult();
					})
					.catch((err) => {
						console.error(err);
					});
			});
	}

	/**
	 * Makes an AJAX call if text field has been submitted
	 */
	private filterContentByText(): void {
		fromEvent(window, MoleculeJobOfferSearchEvents.textInput)
			.pipe(
				map((event: CustomEvent<MoleculeJobOfferSearchEvent>) => {
					return {
						selects: event.detail.selects,
						search: event.detail.search || '',
					};
				}),
			)
			.subscribe((e) => {
				this.loadJobOffers(e)
					.then((response) => {
						this.jobOffersContent = response && response.data;
						this.renderSearchResult();
					})
					.catch((err) => {
						console.error(err);
					});
			});
	}

	/**
	 * Makes an AJAX call when reset button has been clicked
	 */
	private clearFilters(): void {
		fromEvent(window, MoleculeJobOfferSearchEvents.clearButton)
			.pipe(
				map((event: CustomEvent<MoleculeJobOfferSearchEvent>) => {
					return {
						selects: [],
						search: '',
					};
				}),
			)
			.subscribe((e) => {
				this.loadJobOffers(e)
					.then((response) => {
						this.jobOffersContent = response && response.data;
						this.renderSearchResult();
					})
					.catch((err) => {
						console.error(err);
					});
			});
	}

	/**
	 * Fires the AJAX call
	 */
	private async loadJobOffers(e: FilterDataProps) {
		const url = new URL(this.jobOffersUrl);

		e.search && e.search !== '' ? url.searchParams.append('search', e.search) : '';

		e.selects && e.selects.forEach(filter => {
			if (filter.value) {
				url.searchParams.append(filter.key, filter.value)
			}
		})

		const response = await fetch(url.toString());

		return await response.json();
	}

	/**
	 * Renders the new content from AJAX call
	 */
	private renderSearchResult() {
		let jobOffersToRender: string = '';

		this.jobOffersContent.forEach((content) => {
			jobOffersToRender += jobOffersTemplate({
				data: content,
			});
		});

		this.jobOffersWrapper && this.domUtils.renderDomElement('jobOffersWrapper', jobOffersToRender);

		this.initializeNestedComponents();
	}

	/**
	 * Initialeze controllers for new rendered content
	 */
	protected initializeNestedComponents(): void {
		const components = this.jobOffersWrapper.querySelectorAll('[data-controller]');

		components.forEach((element: HTMLElement) => {
			const componentName = <string>element.dataset.controller;
			new ControllerProxy(Controller, componentName, element);
		});
	}
}
